import React from "react";
import { IconBaseProps } from "react-icons";
import {
  AiOutlineHome,
  AiOutlineUserSwitch,
  AiFillDollarCircle,
} from "react-icons/ai";
import { BiSolidFilePdf, BiSolidReport } from "react-icons/bi";
import { IoReceipt, IoReload } from "react-icons/io5";
import { FaUser, FaMinusCircle, FaRegCopy, FaCheck } from "react-icons/fa";
import { FaMoneyCheckDollar, FaGear } from "react-icons/fa6";
import { ImTruck } from "react-icons/im";
import {
  MdSettings,
  MdAdd,
  MdDelete,
  MdOutlineRemoveRedEye,
  MdMenu,
  MdOutlineWbSunny,
  MdOutlineKeyboardArrowUp,
  MdKeyboardArrowDown,
  MdPrintDisabled,
  MdPrint,
  MdOutlinePendingActions,
  MdHistory,
  MdManageAccounts,
  MdLock,
  MdSettingsApplications,
  MdPriceChange,
  MdHub,
  MdBusiness,
  MdPeopleAlt,
  MdStorage,
  MdDashboard,
  MdDownloadForOffline,
  MdRoute,
  MdImportExport
} from "react-icons/md";
import { RiPlayList2Line, RiFileExcel2Fill } from "react-icons/ri";
import {
  IoCloseCircle,
  IoAlertCircleOutline,
  IoArrowUpCircleOutline,
  IoArrowDownCircleOutline,
  IoAddCircleOutline,
  IoEyeOffOutline,
  IoSync,
  IoMoonOutline,
  IoCloseCircleOutline,
  IoClose,
  IoChevronBack,
  IoStorefrontSharp,
} from "react-icons/io5";
import { FiEdit, FiBell } from "react-icons/fi";
import { LuConstruction } from "react-icons/lu";
import { BsLayers } from "react-icons/bs";

// Centralized icon map
const icons = {
  home: AiOutlineHome,
  menu: MdMenu,
  user: FaUser,
  settings: MdSettings,
  more: RiPlayList2Line,
  closeCircle: IoCloseCircle,
  notApplicable: FaMinusCircle,
  add: MdAdd,
  delete: MdDelete,
  edit: FiEdit,
  info: IoAlertCircleOutline,
  upArrowCircle: IoArrowUpCircleOutline,
  downArrowCircle: IoArrowDownCircleOutline,
  addCircle: IoAddCircleOutline,
  eyeOpen: MdOutlineRemoveRedEye,
  eyeOff: IoEyeOffOutline,
  sync: IoSync,
  bell: FiBell,
  userSwitch: AiOutlineUserSwitch,
  sun: MdOutlineWbSunny,
  moon: IoMoonOutline,
  closeCircleOutline: IoCloseCircleOutline,
  upArrow: MdOutlineKeyboardArrowUp,
  downArrow: MdKeyboardArrowDown,
  close: IoClose,
  back: IoChevronBack,
  pdf: BiSolidFilePdf,
  excel: RiFileExcel2Fill,
  construction: LuConstruction,
  printer: MdPrint,
  disabledPrinter: MdPrintDisabled,
  pending: MdOutlinePendingActions,
  copy: FaRegCopy,
  store: IoStorefrontSharp,
  history: MdHistory,
  users: MdManageAccounts,
  lockbox: MdLock,
  configuration: MdSettingsApplications,
  rateMaintenance: MdPriceChange,
  transferStation: MdHub,
  receipt: IoReceipt,
  clientSetup: MdBusiness,
  customers: MdPeopleAlt,
  masters: MdStorage,
  dashboard: MdDashboard,
  post: BsLayers,
  download: MdDownloadForOffline,
  route: MdRoute,
  moneyCheckDollar: FaMoneyCheckDollar,
  gear: FaGear,
  dollarCircle: AiFillDollarCircle,
  solidReport: BiSolidReport,
  truck: ImTruck,
  check: FaCheck,
  importOrExport: MdImportExport,
  reload: IoReload
} as const;

export type IconName = keyof typeof icons;

type IconProps = {
  name: IconName;
  iconAs?: "button" | "icon";
  className?: string;
  onClick?: () => void;
  title?: string;
  tabIndex?: number;
  style?: React.CSSProperties;
  disabled?: boolean;
} & IconBaseProps;

const Icon: React.FC<IconProps> = ({
  onClick,
  title,
  size = 20,
  color = "var(--font-color)",
  className = "",
  iconAs = "button",
  tabIndex,
  style = {},
  disabled = false,
  name,
  ...rest
}) => {
  const IconComponent = icons[name] as any;
  if (!IconComponent) return null;

  const finalColor = disabled ? "var(--icon-disabled-color)" : color;

  const iconNode = (
    <IconComponent
      size={size}
      color={finalColor}
      className={className}
      title={title}
      style={{ verticalAlign: "middle", ...style }}
      {...rest}
    />
  );

  if (iconAs === "button") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`icon-button ${className}`}
        title={title}
        aria-label={title}
        style={style}
        tabIndex={tabIndex ?? -1}
        disabled={disabled}
      >
        {iconNode}
      </button>
    );
  }

  return iconNode;
};

export default Icon;
